import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnExit, PlaidLinkOptions } from 'react-plaid-link';
import { createLinkToken, exchangePublicToken, storePlaidAccessToken } from '../utils/plaidIntegration';

interface PlaidLinkButtonProps {
  onSuccess: (accessToken: string) => void;
  onExit?: () => void;
  buttonText?: string;
  className?: string;
  userId?: string | null;
}

export default function PlaidLinkButton({ 
  onSuccess, 
  onExit, 
  buttonText = '🏦 Connect Bank Account',
  className = '',
  userId = null
}: PlaidLinkButtonProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch link token on component mount
  useEffect(() => {
    const getLinkToken = async () => {
      try {
        setLoading(true);
        const token = await createLinkToken();
        setLinkToken(token);
      } catch (error) {
        alert('Unable to initialize bank connection. Please check your Plaid configuration.');
      } finally {
        setLoading(false);
      }
    };

    getLinkToken();
  }, []);

  // Handle successful Plaid Link
  const handleOnSuccess: PlaidLinkOnSuccess = useCallback(async (public_token, metadata) => {
    try {
      setLoading(true);
      
      // Exchange public token for access token
      const accessToken = await exchangePublicToken(public_token);
      
      // Store access token (in production, store on backend with encryption!)
      storePlaidAccessToken(accessToken, userId);
      
      // Notify parent component
      onSuccess(accessToken);
    } catch (error) {
      alert('Failed to connect bank account. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  // Handle Plaid Link exit
  const handleOnExit: PlaidLinkOnExit = useCallback((err, metadata) => {
    if (onExit) {
      onExit();
    }
  }, [onExit]);

  // Configure Plaid Link
  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  };

  // Initialize Plaid Link
  const { open, ready } = usePlaidLink(config);

  // Handle button click
  const handleClick = () => {
    if (ready) {
      open();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={!ready || loading}
      className={className || `
        px-6 py-3 bg-green-600 text-white rounded-lg font-semibold 
        hover:bg-green-700 transition-colors disabled:opacity-50 
        disabled:cursor-not-allowed flex items-center gap-2
      `}
    >
      {loading ? (
        <>
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading...
        </>
      ) : (
        buttonText
      )}
    </button>
  );
}
